/* =========================================================================
   THG product card photo swiper — mechanics ported from the ID CO card
   gallery (idco-card-gallery.js), adapted to the THG card DOM.

   - Touch: native scroll-snap swiping
   - Desktop: simulated click-and-drag with commit threshold + arrow buttons
   - Forward-only loop via a trailing edge clone: [1..N, cloneFirst]; once a
     swipe settles on the clone we jump instantly to the real first slide —
     seamless because the pixels are identical. Backwards clamps at slide 1.
   - Works in the stacked PLP grid AND inside <scroll-carousel> sections.

   THG's <scroll-carousel> binds no pointer drag of its own (it is a pure
   native scroll container), so the inner track wins the horizontal gesture
   on both touch and mouse. `overscroll-behavior-x: contain` on the track is
   what stops an end-of-strip swipe from chaining out to the outer carousel.

   Real slides are queried dynamically so card-swatch-ajax.js can rewrite the
   track contents on a colorway swap and just call
   window.__refreshProductCardGallery(card) — no re-binding needed.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__thgCardGalleryInit) return;
  window.__thgCardGalleryInit = true;

  function isTouch() {
    return window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  // Theme setting. When off, the strip dead-ends at the last image instead of
  // looping back to the first. A missing global reads as on, so the setting
  // can be absent (older theme, no schema entry) without changing behaviour.
  var LOOP = window.__thgCardGalleryLoop !== false;

  function setupGallery(card) {
    if (card.dataset.galleryBound === '1') return;
    var figure = card.querySelector('.product-card__figure');
    if (!figure) return;
    var track = figure.querySelector('[data-gallery-track]');
    if (!track) return;

    var prev = figure.querySelector('[data-card-prev]');
    var next = figure.querySelector('[data-card-next]');
    var index = 0;           // real slide index, 0..N-1
    var pointerDown = false; // finger or mouse currently on the track

    function realSlides() {
      return track.querySelectorAll('.product-gallery__media:not([data-gallery-clone])');
    }

    // First interaction with the gallery. Deliberately does NOT warm every
    // colorway's images — that pattern caused the iOS memory OOM on TN/ASRV.
    // card-swatch-ajax.js still warms per-swatch on hover/touch.
    var primed = false; // has the shopper actually touched THIS card's gallery

    function prime() {
      hintDone = true; // shopper already knows it swipes — skip the hint
      primed = true;
      warmAhead();     // first contact: get the next slides fetching now
    }

    // Slides ship as loading="lazy" so a grid of cards doesn't pull 5 images
    // each on page load. But a horizontal track sits outside the browser's
    // lazy viewport check, so slides 3+ only began downloading once they were
    // already on screen — the visible pop after the second photo. Flip the
    // next two slides to eager as the shopper moves, so the fetch is already
    // in flight by the time they land.
    //
    // Attribute flip only: no new Image(), no decode(), and the srcset ladder
    // is untouched, so this cannot reintroduce the TN/ASRV memory blowups.
    function warmAhead() {
      // Interaction-gated. applyVisuals() also runs once at bind for every
      // card on the page, and warming there would eagerly fetch 2 extra
      // images per card across the whole grid on load — the ASRV failure.
      // An untouched card must fetch exactly what it fetched before.
      if (!primed) return;
      var all = track.querySelectorAll('.product-gallery__media');
      for (var i = index + 1; i <= index + 2 && i < all.length; i++) {
        var img = all[i];
        if (img && img.tagName !== 'IMG') img = img.querySelector('img');
        if (img && img.getAttribute('loading') === 'lazy') {
          img.setAttribute('loading', 'eager');
          img.removeAttribute('fetchpriority');
        }
      }
    }

    function makeClone(el) {
      var c = el.cloneNode(true);
      c.setAttribute('data-gallery-clone', '');
      c.setAttribute('aria-hidden', 'true');
      c.classList.remove('active');
      return c;
    }

    // [real 0..N-1][cloneFirst] — forward-only loop, no leading clone.
    function buildClones() {
      track.querySelectorAll('[data-gallery-clone]').forEach(function (c) { c.remove(); });
      if (!LOOP) return; // no clone, nothing to wrap onto
      var real = realSlides();
      if (real.length < 2) return;
      track.appendChild(makeClone(real[0]));
    }

    function applyVisuals() {
      var media = realSlides();
      for (var i = 0; i < media.length; i++) {
        media[i].classList.toggle('active', i === index);
      }
      var dots = figure.querySelector('[data-gallery-rail]');
      if (dots) {
        if (media.length < 2) {
          dots.style.display = 'none';
        } else {
          dots.style.display = '';
          // A colorway swap can change the image count, so reconcile the dot
          // count against the live slides before marking one active.
          while (dots.children.length > media.length) dots.removeChild(dots.lastElementChild);
          while (dots.children.length < media.length) {
            var dot = document.createElement('span');
            dot.className = 'pc-dot';
            dots.appendChild(dot);
          }
          for (var d = 0; d < dots.children.length; d++) {
            dots.children[d].classList.toggle('is-active', d === index);
          }
        }
      }
      warmAhead();

      var looping = media.length > 1;
      if (prev) prev.classList.toggle('is-ready', looping && index > 0);
      if (next) next.classList.toggle('is-ready', looping && (LOOP || index < media.length - 1));
    }

    // Position by exact fractional geometry. offsetLeft/clientWidth are
    // integer-rounded while the real layout is fractional, so anything
    // rounded leaves a subpixel sliver of the neighbouring slide visible.
    function exactLeft(el) {
      return el.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    }

    function slideWidth() {
      var first = track.querySelector('.product-gallery__media');
      var w = first ? first.getBoundingClientRect().width : 0;
      return w || track.clientWidth || 1;
    }

    function scrollToPhysical(p, smooth) {
      var kids = track.querySelectorAll('.product-gallery__media');
      var left = kids[p] ? exactLeft(kids[p]) : p * slideWidth();
      track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
    }

    // Belt and suspenders: the clone is only visible while a gesture or
    // animation is in flight. At rest it is hidden, so no rounding artifact
    // can bleed a hairline of the first image into the card's right edge.
    function setClonesVisible(on) {
      track.querySelectorAll('[data-gallery-clone]').forEach(function (c) {
        c.style.visibility = on ? '' : 'hidden';
      });
    }

    function wrappedScrollTo(i, smooth) {
      // Forward-only loop: no backward wrap — clamp at the first slide.
      // With looping off, clamp at the last real slide as well.
      var target = LOOP ? i : Math.min(i, realSlides().length - 1);
      scrollToPhysical(Math.max(0, target), smooth);
    }

    function physicalPos() {
      return Math.round(track.scrollLeft / slideWidth());
    }

    // If we're parked on the clone, jump instantly to the real first slide
    // (identical pixels, invisible). Called after a swipe settles AND at the
    // start of every new gesture — otherwise a fast follow-up swipe would
    // launch from the clone and smooth-scroll all the way back.
    function normalizeNow() {
      var n = realSlides().length;
      if (n < 2) return;
      var pos = physicalPos();
      var kids = track.querySelectorAll('.product-gallery__media');
      if (pos >= n) {
        track.scrollLeft = kids[0] ? exactLeft(kids[0]) : 0;
        index = 0;
      }
    }

    function normalize() {
      if (pointerDown) return;
      if (track.dataset.hintActive === '1') return;
      normalizeNow();
      // Re-align exactly on every settle — kills residual fractions from
      // native snap rounding or mid-flight layout shifts.
      scrollToPhysical(index, false);
      setClonesVisible(false);
    }

    var settleTimer = null;
    function onScroll() {
      if (track.clientWidth === 0) return;
      var n = realSlides().length;
      if (n === 0) return;
      var pos = physicalPos();
      var newIndex = pos >= n ? (LOOP ? 0 : n - 1) : Math.max(0, pos);
      if (newIndex !== index) {
        index = newIndex;
        applyVisuals();
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(normalize, 150);
    }

    track.addEventListener('scroll', onScroll, { passive: true });

    if (prev) {
      prev.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (index === 0) return; // forward-only: nothing behind the first slide
        prime();
        setClonesVisible(true);
        normalizeNow();
        wrappedScrollTo(index - 1, true);
      });
    }
    if (next) {
      next.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        prime();
        setClonesVisible(true);
        normalizeNow();
        wrappedScrollTo(index + 1, true);
      });
    }

    // Kill native image drag on the track and the wrapping anchor so mouse
    // events aren't hijacked by the browser's drag-and-drop API.
    track.addEventListener('dragstart', function (ev) { ev.preventDefault(); });
    var mediaLink = track.closest('a');
    if (mediaLink) {
      mediaLink.addEventListener('dragstart', function (ev) { ev.preventDefault(); });
    }

    // Desktop click-and-drag (mouse only; touch uses native scroll-snap).
    var dragging = false;
    var startX = 0;
    var startScroll = 0;
    var startIndex = 0;
    var moved = 0;
    var DRAG_THRESHOLD = 5;
    var COMMIT_RATIO = 0.05; // drag > 5% of width commits to next/prev

    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      prime();
      setClonesVisible(true);
      normalizeNow();
      dragging = true;
      pointerDown = true;
      moved = 0;
      startX = e.pageX;
      startScroll = track.scrollLeft;
      startIndex = index;
      track.classList.add('is-grabbing');
    });

    window.addEventListener('mousemove', function (e) {
      if (!dragging) return;
      var delta = e.pageX - startX;
      moved = Math.abs(delta);
      track.scrollLeft = startScroll - delta;
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      pointerDown = false;
      var w = slideWidth();
      var scrollDelta = track.scrollLeft - startScroll;
      var target = startIndex;
      if (Math.abs(scrollDelta) >= w * COMMIT_RATIO) {
        target = startIndex + (scrollDelta > 0 ? 1 : -1);
      }
      // Keep snap disabled through the release animation so scroll-snap
      // doesn't fight the smooth scrollTo and yank us back.
      wrappedScrollTo(target, true);
      setTimeout(function () { track.classList.remove('is-grabbing'); }, 350);
    }
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('mouseleave', endDrag);

    // A real drag must not click through to the PDP.
    track.addEventListener('click', function (e) {
      if (moved > DRAG_THRESHOLD) {
        e.preventDefault();
        e.stopPropagation();
        moved = 0;
      }
    }, true);

    // Track finger presence so normalize() never jumps mid-gesture.
    track.addEventListener('touchstart', function () {
      setClonesVisible(true); normalizeNow(); pointerDown = true; prime();
    }, { passive: true });
    // Trackpad/wheel horizontal scrolling has no mousedown — reveal the clone there too.
    track.addEventListener('wheel', function () { setClonesVisible(true); }, { passive: true });
    track.addEventListener('touchend', function () {
      pointerDown = false;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(normalize, 150);
    }, { passive: true });
    track.addEventListener('touchcancel', function () { pointerDown = false; }, { passive: true });

    // Inside a <scroll-carousel> the outer element is a plain native scroller
    // with no drag binding of its own, so touch needs no special handling —
    // the innermost native scroller (this track) wins the gesture and the
    // trailing clone keeps it from ever chaining mid-strip. Stopping mouse
    // events is still cheap insurance if a drag behaviour is added later.
    var outerCarousel = card.closest('scroll-carousel');
    if (outerCarousel) {
      figure.addEventListener('pointerdown', function (e) {
        if (e.target.closest('[data-gallery-track]')) e.stopPropagation();
      });
      figure.addEventListener('mousedown', function (e) {
        if (e.target.closest('[data-gallery-track]')) e.stopPropagation();
      });
    }

    // Initial position: physical slot 0. Retry via rAF until the track has
    // been laid out (cards inside collapsed carousels start at width 0).
    function positionInit(tries) {
      if (track.clientWidth > 0) {
        scrollToPhysical(index, false);
        return;
      }
      if (tries > 0) requestAnimationFrame(function () { positionInit(tries - 1); });
    }

    // Track resizes (image loads, scrollbars, breakpoint shifts) silently
    // invalidate the scroll offset — re-align whenever geometry changes.
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        if (pointerDown || track.classList.contains('is-grabbing')) return;
        scrollToPhysical(index, false);
      }).observe(track);
    }

    buildClones();
    positionInit(60);
    applyVisuals();
    card.dataset.galleryBound = '1';

    // Let card-swatch-ajax.js reset us after it rebuilds the track contents.
    card.__resetGallery = function () {
      index = 0;
      buildClones();
      positionInit(60);
      applyVisuals();
    };
  }

  // One-time swipe hint (touch devices): the first swipeable gallery that
  // dwells at least half-visible in the viewport for ~1.3s gets a gentle
  // nudge-and-return to cue that card images swipe. Once per page load;
  // cancelled for good the moment the shopper swipes one themselves.
  var hintDone = false;

  function runHint(track) {
    var base = track.scrollLeft;
    var nudge = Math.round(track.clientWidth * 0.24);
    var prevSnap = track.style.scrollSnapType;
    track.dataset.hintActive = '1';
    track.style.scrollSnapType = 'none';
    track.scrollTo({ left: base + nudge, behavior: 'smooth' });
    setTimeout(function () {
      track.scrollTo({ left: base, behavior: 'smooth' });
      setTimeout(function () {
        track.style.scrollSnapType = prevSnap;
        delete track.dataset.hintActive;
      }, 450);
    }, 780);
  }

  function setupHint() {
    if (hintDone) return;
    // Real touch devices — or the theme editor's mobile preview, which is a
    // narrow desktop iframe and never matches (hover: none).
    var editorMobile = window.Shopify && window.Shopify.designMode && window.innerWidth < 1000;
    if (!isTouch() && !editorMobile) return;
    if (!('IntersectionObserver' in window)) return;

    var dwell = new Map();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (hintDone) { io.disconnect(); return; }
        var card = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (dwell.has(card)) return;
          dwell.set(card, setTimeout(function () {
            if (hintDone) return;
            var track = card.querySelector('[data-gallery-track]');
            if (!track || track.clientWidth === 0) return;
            if (track.querySelectorAll('.product-gallery__media:not([data-gallery-clone])').length < 2) return;
            hintDone = true;
            io.disconnect();
            runHint(track);
          }, 1350));
        } else {
          var t = dwell.get(card);
          if (t) { clearTimeout(t); dwell.delete(card); }
        }
      });
    }, { threshold: [0, 0.5, 1] });

    document.querySelectorAll('.product-card [data-gallery-track]').forEach(function (tr) {
      var card = tr.closest('.product-card');
      if (card) io.observe(card);
    });
  }

  function init() {
    document.querySelectorAll('.product-card').forEach(setupGallery);
    setupHint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Catch ajax-added cards (pagination / filters / customizer section reloads)
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.classList && n.classList.contains('product-card')) setupGallery(n);
          else if (n.querySelectorAll) n.querySelectorAll('.product-card').forEach(setupGallery);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.__refreshProductCardGallery = function (card) {
    if (!card) return;
    if (card.__resetGallery) card.__resetGallery();
    else setupGallery(card);
  };
})();
